import { useState, useEffect, useCallback } from 'react';
import { IndexedDBService } from '../services/storage/IndexedDBService';

export interface ActivityEvent {
  id: string;
  type: 'journal' | 'ledger' | 'declaration' | 'export' | 'import' | 'validation' | 'calculation' | 'user_action';
  action: string;
  description: string;
  user?: string;
  timestamp: Date;
  status: 'success' | 'warning' | 'error' | 'pending';
  module: string;
  metadata?: Record<string, any>;
}

// Interface pour le stockage (timestamp stocké en string ISO)
interface StoredActivityEvent extends Omit<ActivityEvent, 'timestamp'> {
  timestamp: string;
}

interface ActivityStore {
  activities: ActivityEvent[];
  addActivity: (activity: Omit<ActivityEvent, 'id' | 'timestamp'>) => void;
  markActivityComplete: (id: string, status: 'success' | 'error') => void;
  getRecentActivities: (count?: number) => ActivityEvent[];
  clearActivities: () => void;
  loading: boolean;
}

// Store global pour les activités avec persistance IndexedDB
class ActivityManager {
  private activities: ActivityEvent[] = [];
  private listeners: ((activities: ActivityEvent[]) => void)[] = [];
  private initialized = false;
  private initPromise: Promise<void> | null = null;

  async init(): Promise<void> {
    if (this.initialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = this.loadFromIndexedDB();
    await this.initPromise;
    this.initialized = true;
  }

  private async loadFromIndexedDB(): Promise<void> {
    try {
      const storedActivities = await IndexedDBService.getAll<StoredActivityEvent>('activities');
      
      // Convertir les timestamps string en Date et trier par date décroissante
      this.activities = storedActivities
        .map(a => ({
          ...a,
          timestamp: new Date(a.timestamp)
        }))
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
        .slice(0, 100); // Garder les 100 dernières
      
      this.notifyListeners();
    } catch (error) {
      console.error('Failed to load activities from IndexedDB:', error);
      this.activities = [];
    }
  }

  private async saveToIndexedDB(activity: ActivityEvent): Promise<void> {
    try {
      const storedActivity: StoredActivityEvent = {
        ...activity,
        timestamp: activity.timestamp.toISOString()
      };
      await IndexedDBService.add('activities', storedActivity);
    } catch (error) {
      console.error('Failed to save activity to IndexedDB:', error);
    }
  }

  private async deleteOldActivities(): Promise<void> {
    try {
      // Supprimer les activités au-delà de 100
      if (this.activities.length > 100) {
        const toDelete = this.activities.slice(100);
        for (const activity of toDelete) {
          await IndexedDBService.delete('activities', activity.id);
        }
        this.activities = this.activities.slice(0, 100);
      }
    } catch (error) {
      console.error('Failed to delete old activities:', error);
    }
  }
  
  async addActivity(activity: Omit<ActivityEvent, 'id' | 'timestamp'>): Promise<void> {
    await this.init();
    
    const newActivity: ActivityEvent = {
      ...activity,
      id: `activity_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date()
    };
    
    this.activities.unshift(newActivity);
    
    // Sauvegarder dans IndexedDB
    await this.saveToIndexedDB(newActivity);
    
    // Nettoyer les anciennes activités
    await this.deleteOldActivities();
    
    this.notifyListeners();
  }
  
  async markActivityComplete(id: string, status: 'success' | 'error'): Promise<void> {
    await this.init();
    
    const activityIndex = this.activities.findIndex(a => a.id === id);
    if (activityIndex !== -1) {
      this.activities[activityIndex] = {
        ...this.activities[activityIndex],
        status
      };
      
      // Mettre à jour dans IndexedDB
      try {
        const storedActivity: StoredActivityEvent = {
          ...this.activities[activityIndex],
          timestamp: this.activities[activityIndex].timestamp.toISOString()
        };
        await IndexedDBService.update('activities', storedActivity);
      } catch (error) {
        console.error('Failed to update activity in IndexedDB:', error);
      }
      
      this.notifyListeners();
    }
  }
  
  getActivities(): ActivityEvent[] {
    return [...this.activities];
  }
  
  getRecentActivities(count = 10): ActivityEvent[] {
    return this.activities.slice(0, count);
  }
  
  async clearActivities(): Promise<void> {
    // Supprimer toutes les activités de IndexedDB
    for (const activity of this.activities) {
      try {
        await IndexedDBService.delete('activities', activity.id);
      } catch (error) {
        // Ignorer les erreurs individuelles
      }
    }
    
    this.activities = [];
    this.notifyListeners();
  }
  
  subscribe(listener: (activities: ActivityEvent[]) => void) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }
  
  private notifyListeners() {
    this.listeners.forEach(listener => listener(this.getActivities()));
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}

// Instance globale du gestionnaire d'activités
const activityManager = new ActivityManager();

// Hook pour utiliser le système d'activités
export function useActivities(): ActivityStore {
  const [activities, setActivities] = useState<ActivityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    // Initialiser et charger les activités
    const initAndLoad = async () => {
      setLoading(true);
      await activityManager.init();
      setActivities(activityManager.getActivities());
      setLoading(false);
    };

    initAndLoad();

    // S'abonner aux changements d'activités
    const unsubscribe = activityManager.subscribe(setActivities);
    
    return unsubscribe;
  }, []);
  
  const addActivity = useCallback(async (activity: Omit<ActivityEvent, 'id' | 'timestamp'>) => {
    await activityManager.addActivity(activity);
  }, []);
  
  const markActivityComplete = useCallback(async (id: string, status: 'success' | 'error') => {
    await activityManager.markActivityComplete(id, status);
  }, []);
  
  const getRecentActivities = useCallback((count = 10) => {
    return activityManager.getRecentActivities(count);
  }, []);
  
  const clearActivities = useCallback(async () => {
    await activityManager.clearActivities();
  }, []);
  
  return {
    activities,
    addActivity,
    markActivityComplete,
    getRecentActivities,
    clearActivities,
    loading
  };
}

// Fonction pour obtenir l'utilisateur courant (à utiliser avec useAuth)
let currentUserName = 'Utilisateur';

export function setCurrentUserForActivities(userName: string) {
  currentUserName = userName;
}

export function getCurrentUserForActivities(): string {
  return currentUserName;
}

// Hook spécialisé pour enregistrer automatiquement les activités de journal
export function useJournalActivity() {
  const { addActivity } = useActivities();
  
  const logJournalEntry = useCallback((action: string, description: string, amount?: number, user?: string) => {
    addActivity({
      type: 'journal',
      action,
      description: amount ? `${description} - ${amount.toLocaleString('fr-FR')} CDF` : description,
      user: user || getCurrentUserForActivities(),
      status: 'success',
      module: 'Journal'
    });
  }, [addActivity]);

  const logJournalUpdate = useCallback((description: string, user?: string) => {
    addActivity({
      type: 'journal',
      action: 'Écriture modifiée',
      description,
      user: user || getCurrentUserForActivities(),
      status: 'success',
      module: 'Journal'
    });
  }, [addActivity]);

  const logJournalDelete = useCallback((description: string, user?: string) => {
    addActivity({
      type: 'journal',
      action: 'Écriture supprimée',
      description,
      user: user || getCurrentUserForActivities(),
      status: 'warning',
      module: 'Journal'
    });
  }, [addActivity]);

  const logJournalStateChange = useCallback((action: string, description: string, status: ActivityEvent['status'] = 'success', user?: string) => {
    addActivity({
      type: 'journal',
      action,
      description,
      user: user || getCurrentUserForActivities(),
      status,
      module: 'Journal'
    });
  }, [addActivity]);
  
  const logJournalValidation = useCallback((description: string, count: number) => {
    addActivity({
      type: 'validation',
      action: count > 1 ? 'Validation en lot ADHA' : 'Validation ADHA',
      description: count > 1 ? `${count} écritures validées - ${description}` : description,
      user: 'ADHA AI',
      status: 'success',
      module: 'Agent Comptable'
    });
  }, [addActivity]);
  
  const logJournalExport = useCallback((format: string, period: string, user?: string) => {
    addActivity({
      type: 'export',
      action: `Export ${format.toUpperCase()} généré`,
      description: `Journal des écritures - ${period}`,
      user: user || getCurrentUserForActivities(),
      status: 'success',
      module: 'Export'
    });
  }, [addActivity]);
  
  return {
    logJournalEntry,
    logJournalUpdate,
    logJournalDelete,
    logJournalStateChange,
    logJournalValidation,
    logJournalExport
  };
}

// Hook pour les activités du grand livre
export function useLedgerActivity() {
  const { addActivity } = useActivities();
  
  const logLedgerConsultation = useCallback((account: string, accountName: string, user?: string) => {
    addActivity({
      type: 'ledger',
      action: 'Grand livre consulté',
      description: `Compte ${account} - ${accountName}`,
      user: user || getCurrentUserForActivities(),
      status: 'success',
      module: 'Grand Livre'
    });
  }, [addActivity]);
  
  const logLedgerExport = useCallback((account: string, format: string, user?: string) => {
    addActivity({
      type: 'export',
      action: `Export grand livre ${format.toUpperCase()}`,
      description: `Compte ${account}`,
      user: user || getCurrentUserForActivities(),
      status: 'success',
      module: 'Grand Livre'
    });
  }, [addActivity]);
  
  return {
    logLedgerConsultation,
    logLedgerExport
  };
}

// Hook pour les activités de déclarations
export function useDeclarationActivity() {
  const { addActivity } = useActivities();
  
  const logDeclarationCreated = useCallback((type: string, period: string, user?: string) => {
    addActivity({
      type: 'declaration',
      action: `Déclaration ${type} préparée`,
      description: `${period}`,
      user: user || getCurrentUserForActivities(),
      status: 'success',
      module: 'Déclarations'
    });
  }, [addActivity]);
  
  const logDeclarationSubmitted = useCallback((type: string, period: string, user?: string) => {
    addActivity({
      type: 'declaration',
      action: `Déclaration ${type} soumise`,
      description: `${period} - Transmise aux autorités fiscales`,
      user: user || getCurrentUserForActivities(),
      status: 'success',
      module: 'Déclarations'
    });
  }, [addActivity]);
  
  return {
    logDeclarationCreated,
    logDeclarationSubmitted
  };
}

// Fonctions utilitaires pour l'historique des activités
export function getActivityIcon(type: ActivityEvent['type']) {
  const iconMap = {
    journal: 'FileText',
    ledger: 'BookOpen', 
    declaration: 'FileSpreadsheet',
    export: 'Download',
    import: 'Upload',
    validation: 'CheckCircle',
    calculation: 'Calculator',
    user_action: 'Users'
  };
  
  return iconMap[type] || 'Edit';
}

export function getActivityColor(status: ActivityEvent['status']) {
  const colorMap = {
    success: 'text-green-600 bg-green-50 dark:bg-green-900/20',
    warning: 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20', 
    error: 'text-red-600 bg-red-50 dark:bg-red-900/20',
    pending: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20'
  };
  
  return colorMap[status] || 'text-gray-600 bg-gray-50 dark:bg-gray-900/20';
}

export function getTimeAgo(timestamp: Date): string {
  const now = new Date();
  const diffInMs = now.getTime() - timestamp.getTime();
  const diffInMin = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  
  if (diffInMin < 1) {
    return 'À l\'instant';
  } else if (diffInMin < 60) {
    return `Il y a ${diffInMin} min`;
  } else if (diffInHours < 24) {
    return `Il y a ${diffInHours}h`;
  } else if (diffInDays < 7) {
    return `Il y a ${diffInDays}j`;
  } else {
    return timestamp.toLocaleDateString('fr-FR');
  }
}
